import { LoadingSpinner } from '@components/Loading';
import { Toast, useTrigger } from '@components/Toast';
import { faImage, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CardActionArea, Divider, Stack } from '@mui/material';
import { useDeleteConfiguration } from '@partful/features/configurations/lib/useDeleteConfiguration';
import { useInvalidateQueries } from '@partful/shared/lib/hooks/useInvalidateQueries';
import { Button } from '@partful/shared/ui/Button';
import { useConfirmation } from '@partful/shared/ui/Confirmation/useConfirmation';
import { useSnackBar } from '@partful/shared/ui/SnackBar/useSnackBar';
import { useTenant } from '@providers/Tenant';
import { useUser } from '@providers/User';
import { GA } from '@services/gtag';
import { Configuration, patchConfiguration } from '@services/productInventory';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toBase64 } from '@utils/toBase64';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { StepContent } from 'src/components/StepElements';
import { FormTextField } from 'src/components/TextInput/FormTextField';
import { useConfiguration } from 'src/providers/Configuration';
import { ColourToggle } from './components/ColourToggle';
import { PostProcessingModeToggle } from './components/PostProcessingModeToggle';
import { ProductCategories } from './components/ProductCategories';
import { ProductStatusToggle } from './components/ProductStatusToggle';
import {
  Fields,
  Form,
  LoadingSpinnerContainer,
  SectionTitle,
  Thumbnail,
  ThumbnailAction,
  ThumbnailActionInfo,
  ThumbnailContainer,
  ThumbnailTitleContainer,
} from './components/styles';

export function ProductOverview() {
  const [errorToast, setErrorToast] = useTrigger();
  const { accountId } = useTenant();
  const { role, username } = useUser();
  const { search } = useLocation();
  const history = useHistory();
  const { isUpdatingPartsCategories, isUpdatingSalesBOM, isUpdatingKits, name, thumbnailUrl } = useConfiguration();
  const { productId } = useParams<{ productId: string }>(); // we want the encoded productId here as we just pass it on to create links, so we don't have to decode it using useDecodedParams
  const params = new URLSearchParams(search);
  /* istanbul ignore next */
  const configurationPID = params.get('configurationPID') ?? '';
  const { t: i18n } = useTranslation('manageProduct');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localThumbnail, setLocalThumbnail] = useState<string | undefined>(thumbnailUrl);
  const queryClient = useQueryClient();
  const [displayError, setDisplayError] = useState({ title: '', message: '' });
  const { confirm, close } = useConfirmation();
  const { showSnackBar } = useSnackBar();
  const { title, message } = displayError;

  const { invalidateConfigurationsQuery } = useInvalidateQueries();

  const { isLoading, isError, mutate } = useMutation(
    async (data: Partial<Configuration>) => {
      await patchConfiguration(configurationPID, data, accountId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['getConfiguration']);
        invalidateConfigurationsQuery();
        GA.ManageProductPage.uploadedCustomThumbnail({
          account: accountId,
          configurationPID,
          productName: name,
          role: role!,
          user: username!,
        });
      },
      onError: (e) => {
        const error = e as AxiosError;
        const errorTitle: Record<number, string> = {
          413: i18n('errors.thumbnailTooLargeTitle'),
          400: i18n('errors.generalThumbnailErrorTitle'),
          422: i18n('errors.invalidThumbnailFormatTitle'),
        };
        const errorMessage: Record<number, string> = {
          413: i18n('errors.thumbnailTooLargeMessage'),
          400: i18n('errors.generalThumbnailErrorMessage'),
          422: i18n('errors.invalidThumbnailFormatMessage'),
        };
        // 400 will return general thumbnail errors
        /* istanbul ignore next */
        const errorCode = (error.response?.status as number) || 400;
        /* istanbul ignore next */
        setDisplayError({
          title: errorTitle[errorCode] || 'Unknown error',
          message: errorMessage[errorCode] || 'An error occurred',
        });
        setErrorToast();
      },
    }
  );

  const { mutateAsync: deleteConfiguration, isLoading: isDeleting } = useDeleteConfiguration();

  useEffect(() => {
    setLocalThumbnail(thumbnailUrl);
  }, [thumbnailUrl]);

  useEffect(() => {
    GA.ManageProductPage.landedProductOverview({
      account: accountId,
      configurationPID,
      productName: name,
      role: role!,
      user: username!,
    });
  }, [configurationPID, name, role, username, accountId]);

  if (isUpdatingPartsCategories) {
    return <Redirect to={`/manage-product/${productId}/parts-categories?configurationPID=${configurationPID}`} />;
  }

  if (isUpdatingSalesBOM) {
    return <Redirect to={`/manage-product/${productId}/sales-bom?configurationPID=${configurationPID}`} />;
  }

  /* istanbul ignore next - this needs a test by the kits team */
  if (isUpdatingKits) {
    return <Redirect to={`/manage-product/${productId}/kits?configurationPID=${configurationPID}`} />;
  }

  const onDeleteProduct = async () => {
    confirm({
      title: i18n('productOverview.deleteModal.title', { productName: name }),
      message: i18n('productOverview.deleteModal.content'),
      confirmText: i18n('productOverview.deleteModal.deleteButton'),
      cancelText: i18n('productOverview.deleteModal.cancelButton'),
      onConfirm: async () => {
        await deleteConfiguration({ configurationPID, accountId });
        close();
        showSnackBar({
          title: i18n('productOverview.deleteSuccess.title'),
          message: i18n('productOverview.deleteSuccess.content', { productName: name }),
          severity: 'success',
        });
        history.push('/product-library', {
          successMessage: {
            title: i18n('productOverview.deleteSuccess.title'),
            body: i18n('productOverview.deleteSuccess.content', { productName: name }),
          },
        });
      },
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    // This is a hidden input
    /* istanbul ignore next */
    if (!file) return;

    const base64String = await toBase64(file);

    try {
      mutate({ thumbnail: base64String });
      setLocalThumbnail(base64String);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  /* istanbul ignore next */
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <StepContent>
      <Form>
        <Stack direction="column" spacing={8}>
          <ThumbnailContainer>
            <CardActionArea onClick={handleClick}>
              <div style={{ height: '300px', display: 'flex', justifyContent: 'center' }}>
                {isLoading ? (
                  /* istanbul ignore next */
                  <LoadingSpinnerContainer>
                    <LoadingSpinner height="300px" width="300px" />
                  </LoadingSpinnerContainer>
                ) : (
                  <Thumbnail component="img" image={localThumbnail} />
                )}
              </div>
              <ThumbnailAction sx={{ height: '100px' }}>
                <FontAwesomeIcon icon={faImage} size="lg" />
                <ThumbnailActionInfo>
                  <ThumbnailTitleContainer>
                    {
                      /* istanbul ignore next */
                      isLoading ? i18n('productOverview.uploading') : i18n('productOverview.uploadThumbnail')
                    }
                  </ThumbnailTitleContainer>
                  {i18n('productOverview.uploadThumbnailRecommendation')}
                </ThumbnailActionInfo>
              </ThumbnailAction>
              <input
                data-testid="thumbnailUploadInput"
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/png"
                style={{ display: 'none' }}
              />
            </CardActionArea>
          </ThumbnailContainer>
          <div>
            <Button
              variant="primary"
              type="button"
              size="large"
              data-testid="delete-product-btn"
              disabled={isDeleting}
              onClick={onDeleteProduct}
            >
              <FontAwesomeIcon icon={faTrash} size="xl" />
              {i18n('productOverview.deleteProduct')}
            </Button>
          </div>
        </Stack>
        <Fields>
          <FormTextField
            label={i18n('productOverview.productName')}
            variant="filled"
            type="text"
            value={name}
            fullWidth
            boldLabel
          />

          <ProductCategories configurationPID={configurationPID} />
          <ProductStatusToggle configurationPID={configurationPID} productName={name} />

          <Divider
            sx={{
              margin: '1.5rem 0 1rem 0',
            }}
          />
          <SectionTitle>{i18n('productOverview.renderOptions')}</SectionTitle>

          <ColourToggle configurationPID={configurationPID} />
          <PostProcessingModeToggle configurationPID={configurationPID} />
        </Fields>
      </Form>
      {isError && <Toast dependency={errorToast} title={title} severity="error" message={message} />}
    </StepContent>
  );
}
